import { ReactNode } from 'react';
import { useRouter } from 'next/router';

import useTranslation from '@/utils/hooks/translation/useTranslation';
import { useSocialProfileAccess } from '@/utils/hooks/upsell/useSocialProfileAccess';

import Button from '@/components/base/Button';
import Tooltip from '@/components/base/Tooltip';

type Platform = 'threads' | 'x' | 'youtube';

type ProfileConnectUpsellWrapperProps = {
    children: ReactNode;
    platform: Platform;
    tooltipPosition?: 'top' | 'bottom' | 'left' | 'right';
    customTooltipContent?: ReactNode;
    onUpgradeClick?: () => void;
    showUpgradeButton?: boolean;
};

const ProfileConnectUpsellWrapper = ({
    children,
    platform,
    tooltipPosition = 'top',
    customTooltipContent,
    onUpgradeClick,
    showUpgradeButton = true
}: ProfileConnectUpsellWrapperProps) => {
    const { t } = useTranslation('commons');
    const { hasAccess } = useSocialProfileAccess({ network: platform });
    const router = useRouter();

    const handleUpgradeClick = () => {
        if (onUpgradeClick) {
            onUpgradeClick();
        } else {
            router.push('/settings/subscription-invoices');
        }
    };

    const upsellMessageKey = {
        threads: 'threads_upsell_message',
        x: 'x_upsell_message',
        youtube: 'youtube_upsell_message'
    }[platform];

    const defaultTooltipContent = (
        <div className="flex flex-col gap-3 text-center">
            <p>{t(upsellMessageKey)}</p>
            {showUpgradeButton && (
                <div className="flex justify-center">
                    <Button onClick={handleUpgradeClick} size="sm">
                        {t('upgrade_my_plan')}
                    </Button>
                </div>
            )}
        </div>
    );

    if (hasAccess) {
        return <>{children}</>;
    }

    return (
        <div className="relative">
            <Tooltip
                content={customTooltipContent ?? defaultTooltipContent}
                position={tooltipPosition}
                interactive={true}>
                <div className="flex flex-col">{children}</div>
            </Tooltip>
        </div>
    );
};

export default ProfileConnectUpsellWrapper;
